import { useState, useRef, useCallback, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { useCodeEditorStore, Codebase, GitRepo } from '../stores/codeEditorStore';

const LANGUAGES = [
  { value: 'typescript', label: 'TypeScript' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'cpp', label: 'C++' },
  { value: 'csharp', label: 'C#' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'sql', label: 'SQL' },
  { value: 'json', label: 'JSON' },
  { value: 'markdown', label: 'Markdown' },
];

const SAMPLE_CODE = `// Welcome to HalCode Editor!
// Paste your code below or start typing to see AI-powered analysis

function fibonacci(n: number): number {
  if (n <= 1) return n;
  return fibonacci(n - 1) + fibonacci(n - 2);
}

// Try editing this code to see:
// - Real-time analysis
// - Inline completions
// - Security scanning
// - Complexity metrics

const result = fibonacci(10);
console.log('Fibonacci(10) =', result);`;

export default function CodeEditorPage() {
  const editorRef = useRef<any>(null);
  const completionRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  
  const {
    code,
    language,
    fileName,
    analysis,
    dependencies,
    isAnalyzing,
    showCompletions,
    completions,
    activePanel,
    selectedIssue,
    contextMode,
    selectedCodebase,
    selectedGitRepo,
    codebases,
    gitRepos,
    isLoadingContext,
    setCode,
    setLanguage,
    setFileName,
    setContextMode,
    setSelectedCodebase,
    setSelectedGitRepo,
    loadCodebases,
    loadGitRepos,
    loadFileFromCodebase,
    loadFileFromGit,
    analyzeCode,
    getCompletions,
    setCompletions,
    clearCompletions,
    togglePanel,
    selectIssue,
  } = useCodeEditorStore();

  const [showCompletionDropdown, setShowCompletionDropdown] = useState(false);
  const [completionPosition, setCompletionPosition] = useState({ top: 0, left: 0 });
  const [showCodebaseDropdown, setShowCodebaseDropdown] = useState(false);
  const [showGitDropdown, setShowGitDropdown] = useState(false);
  const [contextFilePath, setContextFilePath] = useState('');

  const handleEditorMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    
    // Configure editor options
    editor.updateOptions({
      fontSize: 14,
      minimap: { enabled: true },
      scrollBeyondLastLine: false,
      automaticLayout: true,
      tabSize: 2,
      wordWrap: 'on',
      lineNumbers: 'on',
      folding: true,
      bracketPairColorization: { enabled: true },
      padding: { top: 16, bottom: 16 },
    });
    
    // Listen for cursor position changes for completions
    editor.onDidChangeCursorPosition((e: any) => {
      const position = e.position;
      const model = editor.getModel();
      if (model) {
        const lineContent = model.getLineContent(position.lineNumber);
        handleCompletionRequest(position.lineNumber, position.column, lineContent);
      }
    });
  };

  const handleCompletionRequest = useCallback((lineNumber: number, column: number, lineContent: string) => {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Debounce completion requests
    debounceRef.current = setTimeout(() => {
      const lastChar = lineContent[column - 2];
      const triggerChars = ['.', '(', ' ', '{', '[', "'", '"'];
      
      if (triggerChars.includes(lastChar) || lineContent.trim().endsWith('.')) {
        getCompletions(lineNumber, column).then(() => {
          if (completions.length > 0) {
            const editor = editorRef.current;
            if (editor) {
              const position = editor.getPosition();
              if (position) {
                const top = position.lineNumber * 20 + 100;
                const left = position.column * 8;
                setCompletionPosition({ top, left });
                setShowCompletionDropdown(true);
              }
            }
          }
        });
      } else {
        clearCompletions();
        setShowCompletionDropdown(false);
      }
    }, 300);
  }, [getCompletions, completions.length, clearCompletions]);

  const handleCodeChange = useCallback((value: string | undefined) => {
    setCode(value || '');
  }, [setCode]);

  const handleRunAnalysis = useCallback(async () => {
    await analyzeCode();
    if (activePanel === 'none') {
      togglePanel('analysis');
    }
  }, [analyzeCode, activePanel, togglePanel]);

  const handleContextModeChange = useCallback((mode: 'standard' | 'codebase' | 'git') => {
    setContextMode(mode);
    setContextFilePath('');
  }, [setContextMode]);

  const handleLoadCodebaseFile = useCallback(async () => {
    if (selectedCodebase && contextFilePath) {
      await loadFileFromCodebase(selectedCodebase.id, contextFilePath);
      setShowCodebaseDropdown(false);
      setContextFilePath('');
    }
  }, [selectedCodebase, contextFilePath, loadFileFromCodebase]);

  const handleLoadGitFile = useCallback(async () => {
    if (selectedGitRepo && contextFilePath) {
      await loadFileFromGit(selectedGitRepo.path, contextFilePath);
      setShowGitDropdown(false);
      setContextFilePath('');
    }
  }, [selectedGitRepo, contextFilePath, loadFileFromGit]);

  const handleApplyFix = (issue: any) => {
    if (issue.fix && editorRef.current) {
      const editor = editorRef.current;
      const model = editor.getModel();
      if (model) {
        const line = issue.line;
        const column = issue.column;
        const lineContent = model.getLineContent(line);
        const newContent = lineContent.substring(0, column - 1) + issue.fix + lineContent.substring(column - 1 + issue.fix.length);
        model.setLineContent(line, newContent);
      }
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error': return 'bg-red-500';
      case 'warning': return 'bg-yellow-500';
      case 'info': return 'bg-blue-500';
      case 'critical': return 'bg-purple-600';
      case 'high': return 'bg-red-600';
      case 'medium': return 'bg-orange-500';
      case 'low': return 'bg-blue-400';
      default: return 'bg-gray-500';
    }
  };

  const getMaintainabilityColor = (score: number) => {
    if (score >= 70) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    return 'text-red-500';
  };

  useEffect(() => {
    // Load codebases and git repos on mount
    loadCodebases();
    loadGitRepos();
    
    const handleClickOutside = (e: MouseEvent) => {
      if (completionRef.current && !completionRef.current.contains(e.target as Node)) {
        setShowCompletionDropdown(false);
      }
      setShowCodebaseDropdown(false);
      setShowGitDropdown(false);
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [loadCodebases, loadGitRepos]);

  return (
    <div className="h-full flex flex-col bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-white">Code Editor</h1>
          
          {/* Context Mode Dropdown */}
          <div className="relative">
            <select
              value={contextMode}
              onChange={(e) => handleContextModeChange(e.target.value as 'standard' | 'codebase' | 'git')}
              className="px-3 py-1 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:border-blue-500"
            >
              <option value="standard">Standard</option>
              <option value="codebase">Codebase</option>
              <option value="git">Git</option>
            </select>
          </div>
          
          {/* Codebase Selector */}
          {contextMode === 'codebase' && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCodebaseDropdown(!showCodebaseDropdown);
                  setShowGitDropdown(false);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="px-3 py-1 bg-gray-700 text-white rounded border border-gray-600 hover:bg-gray-600 focus:outline-none focus:border-blue-500 flex items-center gap-2 cursor-pointer"
              >
                <span>{selectedCodebase?.name || 'Select Codebase'}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showCodebaseDropdown && (
                <div 
                  className="absolute z-50 mt-1 bg-gray-700 border border-gray-600 rounded shadow-xl max-h-64 overflow-y-auto" 
                  style={{ minWidth: '280px', top: '100%' }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {codebases.length === 0 ? (
                    <div className="px-4 py-2 text-gray-400 text-sm cursor-default">No codebases available</div>
                  ) : (
                    codebases.map((codebase: Codebase) => (
                      <div
                        key={codebase.id}
                        className={`px-4 py-2 cursor-pointer hover:bg-gray-600 text-white ${
                          selectedCodebase?.id === codebase.id ? 'bg-blue-600' : ''
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedCodebase(codebase);
                          setShowCodebaseDropdown(false);
                        }}
                      >
                        <div className="font-medium">{codebase.name}</div>
                        <div className="text-xs text-gray-400">{codebase.fileCount} files</div>
                      </div>
                    ))
                  )}
                  {selectedCodebase && (
                    <div className="border-t border-gray-600 p-2">
                      <input
                        type="text"
                        value={contextFilePath}
                        onChange={(e) => setContextFilePath(e.target.value)}
                        placeholder="Enter file path (e.g., src/index.ts)"
                        className="w-full px-2 py-1 bg-gray-800 text-white rounded border border-gray-500 text-sm"
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadCodebaseFile();
                        }}
                        disabled={!contextFilePath}
                        className="mt-2 w-full px-2 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded text-sm cursor-pointer"
                      >
                        Load File
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Git Selector */}
          {contextMode === 'git' && (
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGitDropdown(!showGitDropdown);
                  setShowCodebaseDropdown(false);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="px-3 py-1 bg-gray-700 text-white rounded border border-gray-600 hover:bg-gray-600 focus:outline-none focus:border-blue-500 flex items-center gap-2 cursor-pointer"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                </svg>
                <span>{selectedGitRepo?.name || 'Select Repo'}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showGitDropdown && (
                <div 
                  className="absolute z-50 mt-1 bg-gray-700 border border-gray-600 rounded shadow-xl max-h-64 overflow-y-auto" 
                  style={{ minWidth: '280px', top: '100%' }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {gitRepos.length === 0 ? (
                    <div className="px-4 py-2 text-gray-400 text-sm cursor-default">No git repos available</div>
                  ) : (
                    gitRepos.map((repo: GitRepo) => (
                      <div
                        key={repo.path}
                        className={`px-4 py-2 cursor-pointer hover:bg-gray-600 text-white ${
                          selectedGitRepo?.path === repo.path ? 'bg-blue-600' : ''
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedGitRepo(repo);
                          setShowGitDropdown(false);
                        }}
                      >
                        <div className="font-medium">{repo.name}</div>
                        <div className="text-xs text-gray-400 flex items-center gap-1">
                          <span className="px-1 bg-green-600 rounded text-[10px]">{repo.branch}</span>
                          <span>{repo.path}</span>
                        </div>
                      </div>
                    ))
                  )}
                  {selectedGitRepo && (
                    <div className="border-t border-gray-600 p-2">
                      <input
                        type="text"
                        value={contextFilePath}
                        onChange={(e) => setContextFilePath(e.target.value)}
                        placeholder="Enter file path (e.g., src/index.ts)"
                        className="w-full px-2 py-1 bg-gray-800 text-white rounded border border-gray-500 text-sm"
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadGitFile();
                        }}
                        disabled={!contextFilePath}
                        className="mt-2 w-full px-2 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded text-sm cursor-pointer"
                      >
                        Load File
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="px-3 py-1 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:border-blue-500"
            placeholder="File name"
          />
          
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="px-3 py-1 bg-gray-700 text-white rounded border border-gray-600 focus:outline-none focus:border-blue-500"
          >
            {LANGUAGES.map((lang) => (
              <option key={lang.value} value={lang.value}>{lang.label}</option>
            ))}
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          {isLoadingContext && (
            <div className="flex items-center gap-2 text-blue-400">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="text-sm">Loading...</span>
            </div>
          )}
          <button
            onClick={handleRunAnalysis}
            disabled={isAnalyzing || !code.trim() || isLoadingContext}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white rounded font-medium transition-colors flex items-center gap-2"
          >
            {isAnalyzing ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Analyzing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Analyze Code
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="flex-1 flex overflow-hidden">
        {/* Monaco Editor */}
        <div className="flex-1 relative">
          <Editor
            height="100%"
            language={language}
            value={code}
            onChange={handleCodeChange}
            onMount={handleEditorMount}
            theme="vs-dark"
            options={{
              minimap: { enabled: true },
              fontSize: 14,
              scrollBeyondLastLine: false,
              automaticLayout: true,
            }}
          />
          
          {/* Inline Completion Dropdown */}
          {showCompletionDropdown && completions.length > 0 && (
            <div
              ref={completionRef}
              className="absolute z-50 bg-gray-800 border border-gray-600 rounded shadow-xl overflow-hidden"
              style={{ top: completionPosition.top, left: Math.min(completionPosition.left, 600) }}
            >
              {completions.map((completion) => (
                <div
                  key={completion.id}
                  className="px-4 py-2 hover:bg-gray-700 cursor-pointer text-white"
                  onClick={() => {
                    if (editorRef.current) {
                      const editor = editorRef.current;
                      editor.trigger('keyboard', 'type', { text: completion.text });
                      clearCompletions();
                      setShowCompletionDropdown(false);
                    }
                  }}
                >
                  <div className="font-medium">{completion.text}</div>
                  <div className="text-sm text-gray-400">{completion.detail}</div>
                  <div className="text-xs text-blue-400 mt-1">Confidence: {Math.round(completion.confidence * 100)}%</div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Analysis Panel */}
        {(activePanel !== 'none' || analysis) && (
          <div className="w-96 bg-gray-800 border-l border-gray-700 flex flex-col">
            {/* Panel Tabs */}
            <div className="flex border-b border-gray-700">
              <button
                onClick={() => togglePanel('analysis')}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                  activePanel === 'analysis' 
                    ? 'text-white bg-gray-700 border-b-2 border-blue-500' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Analysis
              </button>
              <button
                onClick={() => togglePanel('dependencies')}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                  activePanel === 'dependencies' 
                    ? 'text-white bg-gray-700 border-b-2 border-blue-500' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Dependencies
              </button>
            </div>
            
            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {isAnalyzing ? (
                <div className="flex items-center justify-center h-32">
                  <svg className="animate-spin h-8 w-8 text-blue-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              ) : analysis ? (
                <div className="space-y-6">
                  {/* Complexity Metrics */}
                  <div className="bg-gray-700 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-white mb-3">Complexity</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-gray-600 rounded">
                        <div className="text-2xl font-bold text-white">{analysis.complexity.cyclomatic}</div>
                        <div className="text-xs text-gray-400">Cyclomatic</div>
                      </div>
                      <div className="text-center p-3 bg-gray-600 rounded">
                        <div className={`text-2xl font-bold ${getMaintainabilityColor(analysis.complexity.maintainability)}`}>
                          {Math.round(analysis.complexity.maintainability)}
                        </div>
                        <div className="text-xs text-gray-400">Maintainability</div>
                      </div>
                      <div className="text-center p-3 bg-gray-600 rounded">
                        <div className="text-2xl font-bold text-white">{analysis.complexity.linesOfCode}</div>
                        <div className="text-xs text-gray-400">Lines of Code</div>
                      </div>
                      <div className="text-center p-3 bg-gray-600 rounded">
                        <div className="text-2xl font-bold text-white">{analysis.complexity.cognitive}</div>
                        <div className="text-xs text-gray-400">Cognitive</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Issues */}
                  {analysis.issues.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-3">
                        Issues ({analysis.issues.length})
                      </h3>
                      <div className="space-y-2">
                        {analysis.issues.map((issue, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-lg cursor-pointer border-l-4 ${
                              selectedIssue === issue ? 'bg-gray-600' : 'bg-gray-700'
                            } ${issue.severity === 'error' ? 'border-red-500' : issue.severity === 'warning' ? 'border-yellow-500' : 'border-blue-500'}`}
                            onClick={() => selectIssue(issue === selectedIssue ? null : issue)}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${getSeverityColor(issue.severity)} text-white`}>
                                {issue.severity}
                              </span>
                              <span className="text-xs text-gray-400">Line {issue.line}</span>
                            </div>
                            <p className="text-sm text-white">{issue.message}</p>
                            {issue.fix && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApplyFix(issue);
                                }}
                                className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                              >
                                Apply Fix →
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Security Issues */}
                  {analysis.security.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-3">
                        Security ({analysis.security.length})
                      </h3>
                      <div className="space-y-2">
                        {analysis.security.map((issue, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-lg bg-gray-700 border-l-4 ${getSeverityColor(issue.severity)}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${getSeverityColor(issue.severity)} text-white`}>
                                {issue.severity}
                              </span>
                              <span className="text-xs text-gray-400">Line {issue.line}</span>
                            </div>
                            <p className="text-sm text-white">{issue.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{issue.cwe}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Suggestions */}
                  {analysis.suggestions.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-3">
                        Suggestions ({analysis.suggestions.length})
                      </h3>
                      <div className="space-y-2">
                        {analysis.suggestions.map((suggestion, index) => (
                          <div key={index} className="p-3 rounded-lg bg-gray-700 border-l-4 border-green-500">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs px-2 py-0.5 rounded bg-green-500 text-white">
                                {suggestion.type}
                              </span>
                              <span className="text-xs text-gray-400">Line {suggestion.line}</span>
                            </div>
                            <p className="text-sm text-white">{suggestion.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {analysis.issues.length === 0 && 
                   analysis.security.length === 0 && 
                   analysis.suggestions.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>No issues found! 🎉</p>
                    </div>
                  )}
                </div>
              ) : activePanel === 'dependencies' ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-white">Dependencies</h3>
                  {dependencies.length > 0 ? (
                    <div className="space-y-2">
                      {dependencies.map((dep, index) => (
                        <div key={index} className="p-3 bg-gray-700 rounded flex items-center gap-3">
                          <span className={`text-xs px-2 py-1 rounded ${
                            dep.type === 'import' ? 'bg-blue-500' : 
                            dep.type === 'require' ? 'bg-yellow-500' : 'bg-purple-500'
                          } text-white`}>
                            {dep.type}
                          </span>
                          <span className="text-sm text-white font-mono">{dep.to}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400">No dependencies found</p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <p>Paste some code and click "Analyze Code" to get started</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
